import { useEffect, useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import i18n from 'i18next'
import { CheckCircle2, CircleCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from 'react-router-dom'
import { toast } from 'sonner'
import {
  SUPPORTED_LANGUAGES,
  isSupportedLanguage,
} from '@/shared/config/languages'
import {
  useGetMachineryDirectiveCompliance,
  useListStandardApplicability,
  useUpdateMachineryDirectiveComplianceCompleted,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useGetProduct } from '@/shared/hooks/api/product'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { NotFoundView } from '@/features/errors/not-found-view'
import { ComplianceStepsNav } from '../components/ComplianceStepsNav'
import { MachineryDirectiveOnboarding } from '../components/MachineryDirectiveOnboarding'
import { MarkCompleteComplianceDialog } from '../components/MarkCompleteComplianceDialog'
import { useMachineryDirectiveComplianceOnboarding } from '../hooks/useMachineryDirectiveComplianceOnboarding'
import { buildMachineryDirectiveStudioBaseHref } from '../machineryDirectiveStudioPaths'

const COMPLIANCE_STUDIO_NAMESPACES = [
  'machineryDirectiveCompliance',
  'productCatalog',
  'associatedParties',
  'countries',
] as const

function LayoutShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Header fixed>
        <Search />
        <div className='ms-auto flex items-center space-x-4'>
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>
      <Main fluid>{children}</Main>
    </>
  )
}

function MachineryDirectiveStudioLoadingShell() {
  return (
    <div className='flex flex-1 flex-col space-y-2 overflow-hidden md:space-y-2 lg:flex-row lg:space-y-0 lg:space-x-12'>
      <aside
        className='top-0 flex shrink-0 flex-col gap-2 lg:sticky lg:w-56'
        aria-hidden
      >
        <Skeleton className='h-4 w-3/4' />
        <Skeleton className='h-5 w-full' />
        <div className='mt-4 flex flex-col gap-2'>
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className='h-8 w-full' />
          ))}
        </div>
      </aside>
      <div className='flex w-full overflow-y-auto p-1'>
        <div className='w-full scrollbar-gutter-stable overflow-x-hidden 2xl:max-w-7xl'>
          <div className='flex flex-col gap-4 p-6'>
            <Skeleton className='h-32 w-full max-w-2xl' />
            <Skeleton className='h-48 w-full max-w-2xl' />
          </div>
        </div>
      </div>
    </div>
  )
}

export function MachineryDirectiveComplianceStudioLayout() {
  const params = useParams<{
    productId: string
    machineryDirectiveComplianceId: string
  }>()
  const productId = params.productId ?? ''
  const machineryDirectiveComplianceId =
    params.machineryDirectiveComplianceId ?? ''
  const studioBaseHref = buildMachineryDirectiveStudioBaseHref(
    productId,
    machineryDirectiveComplianceId
  )
  const backHref = `/product-catalog/${productId}`
  const backLabelKey = 'nav.backToProduct'
  const { pathname } = useLocation()
  const { t } = useTranslation('machineryDirectiveCompliance')
  const [namespacesReady, setNamespacesReady] = useState(false)

  useEffect(() => {
    void i18n.loadNamespaces([...COMPLIANCE_STUDIO_NAMESPACES]).then(() => {
      setNamespacesReady(true)
    })
  }, [])

  const { data: product, isLoading: isLoadingProduct } =
    useGetProduct(productId)
  const {
    data: compliance,
    isLoading: isLoadingCompliance,
    isError: isComplianceError,
    error: complianceError,
  } = useGetMachineryDirectiveCompliance(machineryDirectiveComplianceId)
  const {
    data: standards = [],
    isLoading: isLoadingStandards,
    isError: isStandardsError,
  } = useListStandardApplicability(machineryDirectiveComplianceId)
  const directiveTitle = useMemo(
    () => product?.name?.trim() ?? null,
    [product?.name]
  )

  const contentLanguageLabel = useMemo(() => {
    const contentLanguage = compliance?.contentLanguage
    if (contentLanguage == null || !isSupportedLanguage(contentLanguage)) {
      return null
    }
    return (
      SUPPORTED_LANGUAGES.find((language) => language.value === contentLanguage)
        ?.label ?? contentLanguage
    )
  }, [compliance?.contentLanguage])

  const onboarding = useMachineryDirectiveComplianceOnboarding({
    machineryDirectiveComplianceId,
    backHref,
    compliance,
    standards,
  })

  const {
    showOnboarding,
    allDecidedButNoneAccepted,
    onboardingStep,
    furthestProgressSidebarStepId,
    highRiskCheck,
    highRiskUserOverrideReason,
    userElevatedHighRiskLocked,
    standardsDiscoveryCompleted,
    isTypeCReviewCompleted,
    workflowResultIsHighRisk,
    isHighRiskWorkflowRunning,
    highRiskWorkflowError,
    isStandardsWorkflowRunning,
    standardsWorkflowError,
    standardsDiscoveryTaskState,
    isTypeCStandardCheckRunning,
    typeCStandardCheckError,
    typeCStandardCheckTaskState,
    machineCharacteristicsExtractionCompleted,
    isMachineCharacteristicsExtractionBegun,
    isMachineCharacteristicsExtractionRunning,
    machineCharacteristicsExtractionError,
    machineCharacteristicsExtractionTaskState,
    isBackDisabled,
    onBack,
    onNavigateToSidebarStep,
    onBeginMachineCharacteristicsExtraction,
    onContinueFromIntro,
    onContinueWithEPlan,
    onContinueWithoutEPlan,
    isTypeBGenerateBlocked,
    onBeginHighRisk,
    onCloseHighRisk,
    onHighRiskClassificationUpdated,
    onContinueFromHighRiskResult,
    onContinueFromMachineCompletion,
    onContinueFromMachineLocations,
    onBeginStandards,
    onContinueFromStandardsReview,
    onRerunTypeCStandardCheck,
    onCancelStandardsDiscovery,
    onCancelTypeCStandardCheck,
  } = onboarding

  const [markCompleteDialogOpen, setMarkCompleteDialogOpen] = useState(false)
  const updateCompleted = useUpdateMachineryDirectiveComplianceCompleted(
    machineryDirectiveComplianceId
  )

  const isCompliance404 =
    isComplianceError &&
    isAxiosError(complianceError) &&
    complianceError.response?.status === 404

  if (!namespacesReady || isLoadingProduct) {
    return (
      <LayoutShell>
        <MachineryDirectiveStudioLoadingShell />
      </LayoutShell>
    )
  }

  if (!product) {
    return (
      <LayoutShell>
        <div className='flex flex-1 flex-col items-center justify-center gap-4 p-8'>
          <p className='text-muted-foreground'>{t('errors.productNotFound')}</p>
          <Button asChild variant='outline'>
            <Link to='/product-catalog'>{t('nav.goToProducts')}</Link>
          </Button>
        </div>
      </LayoutShell>
    )
  }

  if (isLoadingCompliance || isLoadingStandards) {
    return (
      <LayoutShell>
        <MachineryDirectiveStudioLoadingShell />
      </LayoutShell>
    )
  }

  if (isCompliance404) {
    return (
      <LayoutShell>
        <div className='flex flex-1 flex-col items-center justify-center p-8'>
          <NotFoundView
            showCode={false}
            title={t('errors.complianceNotFound')}
            primaryAction={{
              label: t(backLabelKey),
              to: backHref,
            }}
          />
        </div>
      </LayoutShell>
    )
  }

  if (isComplianceError) {
    return (
      <LayoutShell>
        <div className='flex flex-1 flex-col items-center justify-center gap-4 p-8'>
          <p className='text-muted-foreground'>Something went wrong.</p>
          <Button asChild variant='outline'>
            <Link to={backHref}>{t(backLabelKey)}</Link>
          </Button>
        </div>
      </LayoutShell>
    )
  }

  if (isStandardsError) {
    return (
      <LayoutShell>
        <div className='flex flex-1 flex-col items-center justify-center gap-4 p-8'>
          <p className='text-muted-foreground'>Something went wrong.</p>
          <Button asChild variant='outline'>
            <Link to={backHref}>{t(backLabelKey)}</Link>
          </Button>
        </div>
      </LayoutShell>
    )
  }

  if (showOnboarding) {
    return (
      <LayoutShell>
        <div className='flex flex-1 flex-col overflow-hidden lg:mx-auto lg:max-w-7xl'>
          <MachineryDirectiveOnboarding
            complianceId={machineryDirectiveComplianceId}
            productId={productId}
            productName={directiveTitle}
            backHref={backHref}
            standards={standards}
            standardsDiscoveryCompleted={standardsDiscoveryCompleted}
            isTypeCReviewCompleted={isTypeCReviewCompleted}
            onboardingStep={onboardingStep}
            furthestProgressSidebarStepId={furthestProgressSidebarStepId}
            highRiskCheck={highRiskCheck}
            highRiskUserOverrideReason={highRiskUserOverrideReason}
            userElevatedHighRiskLocked={userElevatedHighRiskLocked}
            workflowResultIsHighRisk={workflowResultIsHighRisk}
            isHighRiskWorkflowRunning={isHighRiskWorkflowRunning}
            highRiskWorkflowError={highRiskWorkflowError}
            isStandardsWorkflowRunning={isStandardsWorkflowRunning}
            standardsWorkflowError={standardsWorkflowError}
            standardsDiscoveryTaskState={standardsDiscoveryTaskState}
            isTypeCStandardCheckRunning={isTypeCStandardCheckRunning}
            typeCStandardCheckError={typeCStandardCheckError}
            typeCStandardCheckTaskState={typeCStandardCheckTaskState}
            machineCharacteristicsExtractionCompleted={
              machineCharacteristicsExtractionCompleted
            }
            isMachineCharacteristicsExtractionBegun={
              isMachineCharacteristicsExtractionBegun
            }
            isMachineCharacteristicsExtractionRunning={
              isMachineCharacteristicsExtractionRunning
            }
            machineCharacteristicsExtractionError={
              machineCharacteristicsExtractionError
            }
            machineCharacteristicsExtractionTaskState={
              machineCharacteristicsExtractionTaskState
            }
            isPartlyCompletedMachine={compliance?.isPartlyCompletedMachine}
            isBackDisabled={isBackDisabled}
            onBack={onBack}
            onNavigateToSidebarStep={onNavigateToSidebarStep}
            onContinueFromIntro={onContinueFromIntro}
            onContinueWithEPlan={onContinueWithEPlan}
            onContinueWithoutEPlan={onContinueWithoutEPlan}
            isTypeBGenerateBlocked={isTypeBGenerateBlocked}
            onBeginMachineCharacteristicsExtraction={
              onBeginMachineCharacteristicsExtraction
            }
            onBeginHighRisk={onBeginHighRisk}
            onCloseHighRisk={onCloseHighRisk}
            onHighRiskClassificationUpdated={onHighRiskClassificationUpdated}
            onContinueFromHighRiskResult={onContinueFromHighRiskResult}
            onContinueFromMachineCompletion={onContinueFromMachineCompletion}
            onContinueFromMachineLocations={onContinueFromMachineLocations}
            onBeginStandards={onBeginStandards}
            onContinueFromStandardsReview={onContinueFromStandardsReview}
            onRerunTypeCStandardCheck={onRerunTypeCStandardCheck}
            onCancelStandardsDiscovery={onCancelStandardsDiscovery}
            onCancelTypeCStandardCheck={onCancelTypeCStandardCheck}
          />
        </div>
      </LayoutShell>
    )
  }

  const handleMarkCompleteConfirm = async () => {
    try {
      await updateCompleted.mutateAsync({ completed: true })
      toast.success(t('markComplete.dialog.successToast'))
      setMarkCompleteDialogOpen(false)
    } catch {
      toast.error(t('markComplete.dialog.failedToast'))
    }
  }

  const isAllowedWhenRestricted =
    pathname === studioBaseHref ||
    pathname === `${studioBaseHref}/standards` ||
    pathname.startsWith(`${studioBaseHref}/settings`)

  if (allDecidedButNoneAccepted && !isAllowedWhenRestricted) {
    return <Navigate to={studioBaseHref} replace />
  }

  const isCompleted = compliance?.completed
  const isPartlyCompletedMachine = compliance?.isPartlyCompletedMachine

  return (
    <LayoutShell>
      <div className='flex flex-1 flex-col space-y-2 overflow-hidden md:space-y-2 lg:flex-row lg:space-y-0 lg:space-x-12'>
        <aside className='top-0 flex shrink-0 flex-col gap-2 lg:sticky lg:w-56'>
          <ComplianceStepsNav
            studioBaseHref={studioBaseHref}
            backHref={backHref}
            directiveTitle={directiveTitle}
            isStandardApplicabilityDone={true}
            restrictToIntroAndStandards={allDecidedButNoneAccepted}
            isPartlyCompletedMachine={isPartlyCompletedMachine}
          />
          {compliance && !isCompleted && (
            <Button
              variant='outline'
              size='sm'
              className='w-full justify-start'
              onClick={() => {
                setMarkCompleteDialogOpen(true)
              }}
            >
              <CircleCheck className='me-2 size-4 shrink-0' aria-hidden />
              {t('markComplete.button')}
            </Button>
          )}
          {isCompleted && (
            <Badge
              variant='default'
              className='w-full justify-center gap-1.5 bg-green-600 py-1.5 font-medium text-white'
            >
              <CheckCircle2 className='size-4' aria-hidden />
              {t('markComplete.completedLabel')}
            </Badge>
          )}
          {contentLanguageLabel != null ? (
            <Badge variant='secondary' className='w-full justify-center py-1.5'>
              {t('studioContentLanguage.badge', {
                language: contentLanguageLabel,
              })}
            </Badge>
          ) : null}
        </aside>
        <div className='flex w-full overflow-y-auto p-1'>
          <main className='w-full scrollbar-gutter-stable overflow-x-hidden 2xl:max-w-7xl'>
            <Outlet />
          </main>
        </div>
      </div>
      <MarkCompleteComplianceDialog
        isOpen={markCompleteDialogOpen}
        onClose={() => {
          setMarkCompleteDialogOpen(false)
        }}
        onConfirm={handleMarkCompleteConfirm}
        isSubmitting={updateCompleted.isPending}
      />
    </LayoutShell>
  )
}
